import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { useToast } from "@/components/feedback";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { useTenantData } from "@/hooks/shared/useTenantData";
import { createAuthenticatedApi } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import type { UpdateTuitionRateTierRequest } from "@/openapi/generated";
import { TuitionTiersApi } from "@/openapi/generated";
import { invalidateTuitionRatePlanQueries } from "./invalidate-tuition-rate-queries";

/** GET still returns null max for an open-ended last tier; the generated PUT type does not. */
type UpdateTuitionRateTierPayload = Omit<
  UpdateTuitionRateTierRequest,
  "maxQuantity"
> & {
  maxQuantity: number | null;
};

export function useTuitionRateTierUpdate(toast: ReturnType<typeof useToast>) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async ({
      planId,
      tierId,
      payload,
    }: {
      planId: number;
      tierId: number;
      payload: UpdateTuitionRateTierPayload;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const api = createAuthenticatedApi(TuitionTiersApi, accessToken);
      return api.apiTuitionTiersRatePlansPlanIdTiersTierIdPut({
        planId,
        tierId,
        updateTuitionRateTierRequest: payload as UpdateTuitionRateTierRequest,
        xTenantID: tenantId,
      });
    },
    onSuccess: async () => {
      await invalidateTuitionRatePlanQueries(queryClient);
      toast({
        title: "Tier updated",
        description: "Pricing tier updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(
        error,
        "Failed to update pricing tier.",
      );
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
}
